import { zodResolver } from '@hookform/resolvers/zod';

import { useForm } from 'react-hook-form';

import pMap from 'p-map';

import { Typography, Divider } from '@mui/material';

import { useQuery } from '@apollo/client/react';

import { CREATE_POST_MUTATION } from '@/app/(main)/g/[machineName]/noticeboard/_components/noticeboard/NoticeboardPoster';
import { graphql, type DocumentType } from '@/gql';
import { useOr2Editor } from '@/modules/editor/hooks/useOr2Editor';
import type { ShareToNoticeboardFormData } from '@/modules/group/noticeboard/ShareToNoticeboardForm';
import {
  ShareToNoticeboardForm,
  ShareToNoticeboardSchema,
} from '@/modules/group/noticeboard/ShareToNoticeboardForm';
import { Or2Button } from '@/components/button/Or2Button';
import { Or2Dialog } from '@/components/feedback/Or2Dialog';
import { useMutation } from '@/hooks/mutationWithSnackbar';
import { useGetFileUrl } from '@/modules/files/hooks/useGetFileUrl';
import { extractConnectionProps } from '@/utils/queries/queryExtracts';
import type { PostsEnabledFilterGroup } from '@/modules/post/CreatePostModal';

const SHARE_FILE_TO_NOTICEBOARD_QUERY = graphql(`
  query ShareFileToNoticeboard($serviceId: ID) {
    service(id: $serviceId) {
      serviceGroupsConnection {
        edges {
          node {
            _id
            name
            iconUrl
            type {
              hasPosts
            }
          }
        }
      }
    }
  }
`);

const SHARE_FILE_ON_NOTICEBOARD_DIALOG_FILE_FRAGMENT = graphql(`
  fragment ShareFileToNoticeboardDialogFileFields on File {
    _id
    filename
    url
  }
`);

const SHARE_FILE_ON_NOTICEBOARD_DIALOG_FILES_FOR_OWNER_FRAGMENT = graphql(`
  fragment ShareFileToNoticeboardDialogFilesForOwnerFields on FilesForOwner {
    owner {
      ... on Service {
        _id
        machineName
      }
    }
  }
`);

interface Props {
  file: DocumentType<typeof SHARE_FILE_ON_NOTICEBOARD_DIALOG_FILE_FRAGMENT>;
  filesForOwner: DocumentType<
    typeof SHARE_FILE_ON_NOTICEBOARD_DIALOG_FILES_FOR_OWNER_FRAGMENT
  >;
  open: boolean;
  onClose: () => void;
}

export const ShareFileToNoticeboardDialog = ({
  file,
  filesForOwner,
  open,
  onClose,
}: Props) => {
  const { filename } = file;
  const { owner } = filesForOwner;

  const serviceId = owner.__typename === 'Service' ? owner?._id : '';
  const machineName =
    owner.__typename === 'Service' && owner?.machineName
      ? owner.machineName
      : '';

  const getFileUrl = useGetFileUrl({ type: 'Service', machineName });

  const { control, handleSubmit, setValue, watch, reset } =
    useForm<ShareToNoticeboardFormData>({
      defaultValues: {
        htmlContent: `Have a look at this:<br /><a href="${getFileUrl(file)}">${filename ?? ''}</a>`,
        groups: [],
      },
      resolver: zodResolver(ShareToNoticeboardSchema),
    });

  const { loading: loadingQuery, data } = useQuery(
    SHARE_FILE_TO_NOTICEBOARD_QUERY,
    {
      variables: {
        serviceId,
      },
      skip: !serviceId,
      fetchPolicy: 'cache-and-network',
    },
  );

  const [createPost, { loading: loadingMutation }] = useMutation(
    CREATE_POST_MUTATION,
    {
      completionMessage: 'Post created',
      onCompleted: onClose,
    },
  );

  const loading = loadingQuery || loadingMutation;

  const editor = useOr2Editor({
    htmlContent: watch('htmlContent'),
    editable: !loading,
    onChange: (htmlContent) => setValue('htmlContent', htmlContent),
    immediatelyRender: true,
  });

  const onSubmit = async (data: ShareToNoticeboardFormData) => {
    await pMap(
      data.groups,
      async (group) => {
        await createPost({
          variables: {
            data: {
              htmlContent: data.htmlContent,
              content: null,
              editorContent: null,
              embeds: null,
              notifications: null,
              title: null,
              group: group,
              subgroup: null,
            },
          },
        });
      },
      { concurrency: 3 },
    );
  };

  const service = data?.service;
  const groupOptions: PostsEnabledFilterGroup[] = extractConnectionProps(
    service?.serviceGroupsConnection,
  ).map((g) => {
    return {
      ...g,
      hasPosts: g.type?.hasPosts ?? null,
    };
  });

  return (
    <Or2Dialog
      keepMounted
      title="Share file to noticeboard"
      loading={loading}
      actions={
        <>
          <Or2Button color="secondary" onClick={onClose}>
            Cancel
          </Or2Button>
          <Or2Button onClick={handleSubmit(onSubmit)}>Share</Or2Button>
        </>
      }
      slotProps={{
        transition: {
          onEnter: () => reset(),
        },
      }}
      open={open}
      onClose={onClose}
    >
      <Typography>Edit the text below:</Typography>
      <Divider />
      <ShareToNoticeboardForm
        control={control}
        editor={editor}
        groupOptions={groupOptions}
      />
    </Or2Dialog>
  );
};
